/**
 * How one scene hands over to the next.
 *
 * Two devices, used for different jobs.
 *
 * The dust is a single event, at the top of the page. It blows across the opening once
 * as the visitor first scrolls, thickens, and clears — and is never seen again. It used
 * to run at every scene boundary, which was a mistake: a strong effect repeated is
 * learned, and once the eye recognises it the third one reads as the page stalling
 * rather than as weather. Spending it once buys the arrival; spending it nine times
 * costs the whole narrative.
 *
 * Everything after that hands over through the haze — a warm lift with no black ground
 * and no texture, over a wider and softer band. It has no shape to recognise, so it can
 * run at every boundary without ever announcing itself. What actually carries those
 * transitions is the parallax the scenes already have; the haze only keeps the seam
 * from showing.
 */

const LAYERS = ["a", "b", "c"] as const;

/**
 * The dust plate, in three copies.
 *
 * One plate cross-fading in is a photograph of smoke, not smoke: the eye reads the
 * frozen curl instantly. Three copies rotating against each other at unrelated rates
 * never present the same arrangement twice, and rotation is what does the work —
 * translating a texture slides a picture, while rotating two layers against each other
 * continuously changes where the dense parts overlap, which is what reads as churn.
 */
export function createVeil(): HTMLElement {
  const veil = document.createElement("div");
  veil.className = "veil";
  veil.setAttribute("aria-hidden", "true");
  for (const name of LAYERS) {
    const layer = document.createElement("span");
    layer.className = `veil__layer veil__layer--${name}`;
    veil.append(layer);
  }
  return veil;
}

/** The haze. No children on purpose: a shape is a thing the eye can learn. */
export function createWash(): HTMLElement {
  const wash = document.createElement("div");
  wash.className = "wash";
  wash.setAttribute("aria-hidden", "true");
  return wash;
}

/* ------------------------------------------------------------------ *
 * The one dust event
 * ------------------------------------------------------------------ */

/** Where the dust begins to gather, where it is thickest, and where it has gone. */
const DUST_FROM = 0.58;
const DUST_PEAK = 0.8;
const DUST_GONE = 0.96;

/**
 * Capped well below opaque. The dust no longer has a cut to hide — the opening simply
 * scrolls away into what follows — so it passes across the frame rather than closing
 * over it. At 1 this would be a black flash, which is a fault, not an atmosphere.
 */
const DUST_PEAK_DENSITY = 0.76;

/**
 * The dust for the opening scene, 0..1, as a pulse within that scene's own run.
 *
 * Self-contained by design. The old veil relied on the *next* scene's head to catch its
 * tail, so anything placed between two scenes made it snap off mid-dissolve. Rising and
 * falling inside one scene means nothing downstream can strand it.
 */
export function openingDust(progress: number): number {
  if (progress <= DUST_FROM || progress >= DUST_GONE) return 0;
  const rising = (progress - DUST_FROM) / (DUST_PEAK - DUST_FROM);
  const falling = (DUST_GONE - progress) / (DUST_GONE - DUST_PEAK);
  const raw = Math.min(1, Math.min(rising, falling));
  // Smoothstep, so the gather and the clearing both ease rather than cornering.
  return raw * raw * (3 - 2 * raw) * DUST_PEAK_DENSITY;
}

/** Whether the plates need to be turning at all. Nine rotating layers are not free. */
export function dustChurning(progress: number): boolean {
  // Slightly ahead of the ramp: starting the animation at the same moment the opacity
  // lifts would show the first frames as a motionless plate fading in, which is exactly
  // the static-picture look the three layers exist to avoid.
  return progress > DUST_FROM - 0.08 && progress < DUST_GONE;
}

/* ------------------------------------------------------------------ *
 * Every other handover
 * ------------------------------------------------------------------ */

const WASH_TAIL_FROM = 0.74;
const WASH_HEAD_UNTIL = 0.24;

/**
 * How strongly the haze sits over one scene, 0..1.
 *
 * `first` suppresses the head and `last` the tail, so the page never loads under haze
 * and never fades into the index, either of which reads as a fault rather than a mood.
 */
export function washIntensity(progress: number, first: boolean, last: boolean): number {
  const tail = last ? 0 : (progress - WASH_TAIL_FROM) / (1 - WASH_TAIL_FROM);
  const head = first ? 0 : (WASH_HEAD_UNTIL - progress) / WASH_HEAD_UNTIL;
  const raw = Math.min(1, Math.max(0, Math.max(tail, head)));
  // A linear ramp on something this faint shows its corners as a crease.
  return raw * raw * (3 - 2 * raw);
}
